<script setup>
import { computed } from 'vue'

const props = defineProps({
  modelValue: {
    type: Number,
    default: 0,
  },
  min: {
    type: Number,
    default: 200,
  },
  max: {
    type: Number,
    default: 1200,
  },
  step: {
    type: Number,
    default: 8,
  },
  label: {
    type: String,
    default: '',
  },
  unit: {
    type: String,
    default: 'mm',
  },
})
const emit = defineEmits(['update:modelValue'])

const normalizedValue = computed(() => Math.min(Math.max(props.modelValue, props.min), props.max))
const rangeSpan = computed(() => Math.max(props.max - props.min, 1))
const percent = computed(() => ((normalizedValue.value - props.min) / rangeSpan.value) * 100)

const updateValue = (value) => {
  const clamped = Math.min(Math.max(Number(value), props.min), props.max)
  emit('update:modelValue', clamped)
  return clamped
}
</script>

<template>
  <div class="single-slider">
    <div class="single-slider__value">
      <div class="single-slider__label">{{ props.label }}</div>
      <div class="single-slider__value-input">
        <input
          type="number"
          :min="props.min"
          :max="props.max"
          :step="props.step"
          :value="normalizedValue"
          @change="(e) => (e.currentTarget.value = updateValue(e.currentTarget.value))"
        />
        <span class="single-slider__unit">{{ props.unit }}</span>
      </div>
    </div>
    <input
      class="slider slider--single"
      type="range"
      :min="props.min"
      :max="props.max"
      :step="props.step"
      :value="normalizedValue"
      :style="{ '--percent': `${percent}%` }"
      @input="updateValue($event.currentTarget.value)"
    />
  </div>
</template>

<style scoped>
.single-slider {
  display: flex;
  flex-direction: column;
  gap: var(--space-16);
}

.single-slider__value {
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
  align-items: center;
}

.single-slider__label {
  margin: 0;
  font-size: 0.85rem;
  font-weight: 400;
  color: var(--semantic-text-default);
}

.single-slider__value-input {
  padding: var(--space-8);
  border: 1px solid var(--semantic-border-muted);
  /* border-radius: 0.2rem; */
  background: var(--color-base-white);
  display: flex;
  align-items: center;
  gap: var(--space-8);
  width: 7rem;
}

.single-slider__value-input input {
  border: none;
  background: transparent;
  font-size: 0.9rem;
  font-weight: 400;
  color: var(--semantic-text-black);
  width: 100%;
}

.single-slider__value-input input:focus-visible {
  outline: none;
}

.single-slider__unit {
  font-size: 0.85rem;
  color: var(--semantic-bg-petrol-strong);
}

.slider--single {
  position: relative;
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 4px;
  border-radius: 999px;
  background: linear-gradient(
    to right,
    var(--semantic-bg-petrol-strong) 0%,
    var(--semantic-bg-petrol-strong) var(--percent),
    var(--semantic-bg-petrol-muted) var(--percent),
    var(--semantic-bg-petrol-muted) 100%
  );
}

.slider--single::-webkit-slider-runnable-track {
  height: 4px;
  border-radius: 999px;
  background: transparent;
}

.slider--single::-moz-range-track {
  height: 4px;
  border-radius: 999px;
  background: var(--semantic-bg-petrol-muted);
}

.slider--single::-moz-range-progress {
  height: 4px;
  border-radius: 999px;
  background: var(--semantic-bg-petrol-strong);
}

.slider--single::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--semantic-bg-petrol-strong);
  margin-top: -9px;
  cursor: pointer;
}

.slider--single::-moz-range-thumb {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--semantic-bg-petrol-strong);
  cursor: pointer;
}

.slider--single:focus-visible {
  outline: none;
}

.slider--single:focus-visible::-webkit-slider-thumb {
  outline: 2px solid var(--semantic-text-muted);
  outline-offset: 4px;
}

.slider--single:focus-visible::-moz-range-thumb {
  outline: 2px solid var(--semantic-text-muted);
  outline-offset: 4px;
}
</style>
